/**
 * Per-assessment bank rows (versions, Leo edits) that must survive refresh.
 * Works with the live paper roster in `assessment-live-paper`.
 */

import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"
import { notifyQuestionRecordsChanged } from "@/lib/question-record/changes"
import type { QuestionRevision } from "@/lib/question-preview"
import type { PaperQuestionRow } from "@/lib/question-preview-adapters"
import {
  mergeHistoryRicher,
  readTieredJsonMerged,
  removeTieredJson,
  removeTieredJsonByMark,
  writeTieredJson,
} from "@/lib/question-version-history/durable-storage"

const STORAGE_MARK = "assessment-question-history:v1:"
const memoryStash = new Map<string, Record<string, AssessmentBankQuestion>>()

function storageKey(assessmentId: string) {
  return `exxat-ds:${STORAGE_MARK}${assessmentId}`
}

function isBankRowMap(value: unknown): value is Record<string, AssessmentBankQuestion> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value))
}

function readAll(assessmentId: string): Record<string, AssessmentBankQuestion> {
  if (!assessmentId) return {}
  const cached = memoryStash.get(assessmentId)
  if (cached) return { ...cached }
  const parsed = readTieredJsonMerged(
    storageKey(assessmentId),
    isBankRowMap,
    mergeHistoryRicher,
  )
  if (!parsed) return {}
  memoryStash.set(assessmentId, parsed)
  return { ...parsed }
}

function writeAll(
  assessmentId: string,
  rows: Record<string, AssessmentBankQuestion>,
) {
  if (!assessmentId) return
  memoryStash.set(assessmentId, rows)
  writeTieredJson(storageKey(assessmentId), rows)
  notifyQuestionRecordsChanged()
}

export function readAssessmentQuestionBankRow(
  assessmentId: string,
  questionId: string,
): AssessmentBankQuestion | undefined {
  return readAll(assessmentId)[questionId]
}

export function readAssessmentQuestionBankRows(
  assessmentId: string,
): Map<string, AssessmentBankQuestion> {
  return new Map(Object.entries(readAll(assessmentId)))
}

export function writeAssessmentQuestionBankRow(
  assessmentId: string,
  row: AssessmentBankQuestion,
) {
  if (!assessmentId || !row.id) return
  const all = readAll(assessmentId)
  all[row.id] = { ...row, id: row.id }
  writeAll(assessmentId, all)
}

export function clearAssessmentQuestionHistory(assessmentId?: string) {
  if (assessmentId) {
    memoryStash.delete(assessmentId)
    removeTieredJson(storageKey(assessmentId))
    notifyQuestionRecordsChanged()
    return
  }
  memoryStash.clear()
  removeTieredJsonByMark(STORAGE_MARK)
  notifyQuestionRecordsChanged()
}

/** Merge session bank rows onto paper roster rows after reload. */
export function mergeAssessmentHistoryIntoPaperRows<
  T extends PaperQuestionRow,
>(assessmentId: string, rows: readonly T[]): T[] {
  const stored = readAll(assessmentId)
  if (Object.keys(stored).length === 0) return [...rows]
  return rows.map((row) => {
    const session = stored[row.id]
    if (!session) return row
    return {
      ...row,
      ...(session.stem ? { stem: session.stem } : {}),
      ...(session.title ? { title: session.title } : {}),
      ...(session.revisions?.length ? { revisions: session.revisions } : {}),
    }
  })
}

export function assessmentSessionRevisions(
  assessmentId: string,
  questionId: string,
): QuestionRevision[] | undefined {
  return readAssessmentQuestionBankRow(assessmentId, questionId)?.revisions
}
